import { useLayoutEffect, useRef, useState } from 'react';
import {
  COLUMNS, type ColorInfo, NEXT_STATUS, type Task, type TaskStatus, colorInfo, colorKeys, daysSince, inScope, localToday,
  matchesSearch, normalize, plural, sortColumn,
} from '@quadro/domain';
import { Icon } from '../../components/Icon';
import { openSheet } from '../../state/sheets';
import { useUI } from '../../state/ui';
import { useWorkspace } from '../../state/workspace';
import { CaptureField } from '../shared/Capture';
import { useEntities } from '../shared/useMap';
import { type CardPos, bumpCounts, counts, flip, rects } from './effects';
import { TaskCard } from './TaskCard';
import { useTaskActions } from './useTaskActions';

/** Colunas do kanban com arrastar e soltar, criação inline e destaque de alertas. */
export function Board({ tasks, highlightIds }: { tasks: Task[]; highlightIds: Set<string> | null }) {
  const ws = useWorkspace();
  const ui = useUI();
  const actions = useTaskActions();
  const ents = useEntities();
  const [adding, setAdding] = useState<TaskStatus | null>(null);
  const [drag, setDrag] = useState<{ id: string; col: TaskStatus | null; before: string | null } | null>(null);
  const boardRef = useRef<HTMLDivElement>(null);
  // Medidas tiradas na renderização (antes de o React mexer na tela): ponto de partida das animações.
  const before = useRef<{ rects: Map<string, CardPos>; counts: Map<string, string>; wip: string | null } | null>(null);
  const entered = useRef(false);
  if (boardRef.current) {
    before.current = { rects: rects(boardRef.current), counts: counts(boardRef.current), wip: boardRef.current.querySelector<HTMLElement>('.wip-bar i')?.style.width ?? null };
  }
  const today = localToday();

  const visible = tasks.filter(t => inScope(t, ui.scope, ws.me.id) && matchesSearch(t, ui.search, normalize));
  const passColor = (t: Task) => !ui.colorFilter.length || ui.colorBy === 'none' || colorKeys(t, ui.colorBy, ws.settings, today).some(k => ui.colorFilter.includes(k));
  const colorOf = (t: Task): ColorInfo | null => (ui.colorBy === 'none' ? null : colorInfo(colorKeys(t, ui.colorBy, ws.settings, today)[0], ui.colorBy, ws.settings, ws.person));
  const showProject = ws.settings.projects.length > 1 && (ui.scope.project === 'all' || ui.scope.project === 'mine');
  const manual = ui.sort === 'manual';

  useLayoutEffect(() => {
    const root = boardRef.current;
    if (!root) return;
    // Entrada em cascata só na primeira vez que o quadro aparece com cartões.
    const entrance = !entered.current && !!root.querySelector('.kcard');
    if (entrance) entered.current = true;
    const b = entrance ? null : before.current;
    flip(root, b?.rects ?? null, { entrance });
    bumpCounts(root, b?.counts ?? new Map(), b?.wip ?? null, entrance);
    before.current = null;
  });

  const beforeCard = (zone: Element, y: number) =>
    [...zone.querySelectorAll<HTMLElement>('.kcard:not(.dragging)')].find(c => { const r = c.getBoundingClientRect(); return y < r.top + r.height / 2; })?.dataset.card ?? null;

  return (
    <div className={`board ${drag ? 'arrastando' : ''}`} id="board" ref={boardRef}
      onDragStart={e => {
        const k = (e.target as HTMLElement).closest<HTMLElement>('.kcard');
        if (!k || !ws.canWrite) return;
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', k.dataset.card!);
        setDrag({ id: k.dataset.card!, col: null, before: null });
        requestAnimationFrame(() => k.classList.add('dragging'));
      }}
      onDragEnd={e => { (e.target as HTMLElement).classList?.remove('dragging'); setDrag(null); }}
      onDragOver={e => {
        if (!drag) return;
        const col = (e.target as HTMLElement).closest<HTMLElement>('.col');
        if (!col) return;
        e.preventDefault();
        const status = col.dataset.col as TaskStatus;
        const before = manual && status !== 'done' ? beforeCard(col.querySelector('.col-cards')!, e.clientY) : null;
        if (drag.col !== status || drag.before !== before) setDrag({ ...drag, col: status, before });
      }}
      onDrop={e => {
        if (!drag?.col) return;
        e.preventDefault();
        const { id, col, before } = drag;
        setDrag(null);
        actions.move(id, col, manual ? before : null);
      }}>
      {COLUMNS.map(col => {
        let list = sortColumn(visible.filter(t => t.status === col.id && passColor(t)), col.id, ui.sort);
        const total = list.length;
        let older = 0;
        if (col.id === 'done' && !ui.showOlderDone) {
          const recent = list.filter(t => daysSince(t.completedAt) <= 14);
          older = list.length - recent.length;
          list = recent;
        }
        const over = drag?.col === col.id;
        const wip = ws.settings.wipLimit;
        const line = <div className="drop-line" key="__line" />;
        const cards = list.map(t => (
          <TaskCard key={t.id} task={t} dim={!!highlightIds && !highlightIds.has(t.id)} color={colorOf(t)} showProject={showProject}
            entityName={ents.name} onOpen={id => openSheet({ kind: 'task', id })}
            onAdvance={id => { const n = NEXT_STATUS[t.status]; if (n) actions.move(id, n); }}
            onComplete={id => actions.move(id, 'done')} />
        ));
        if (over && manual && col.id !== 'done' && drag) {
          const i = drag.before ? list.findIndex(t => t.id === drag.before) : -1;
          cards.splice(i < 0 ? cards.length : i, 0, line);
        }
        return (
          <section key={col.id} className={`col ${list.length ? '' : 'vazia'} ${over ? 'over' : ''}`} data-col={col.id} aria-label={col.name}>
            <header className="col-head" title={col.hint}>
              <span className="col-name">{col.name}</span>
              {col.id === 'doing'
                ? <button className={`col-count ${total > wip ? 'red' : ''}`} disabled={!ws.canWrite} onClick={() => openSheet({ kind: 'wip' })} title={`Limite de tarefas em andamento: ${wip}`}>{total}/{wip}</button>
                : <span className="col-count">{total}</span>}
              {col.id === 'doing' && (
                <div className={`wip-bar ${total > wip ? 'over' : ''}`} title={`${total} de ${wip} no limite de trabalho em andamento`}>
                  <i style={{ width: `${Math.min(100, Math.round((total / Math.max(1, wip)) * 100))}%` }} />
                </div>
              )}
            </header>
            <div className="col-cards">
              {cards.length ? cards : <div className="col-empty">{ui.search ? 'Nada encontrado' : ''}</div>}
              {older > 0 && <button className="older" onClick={() => ui.set({ showOlderDone: true })}>Mostrar {plural(older, 'mais antiga', 'mais antigas')}</button>}
              {col.id === 'done' && ui.showOlderDone && <button className="older" onClick={() => ui.set({ showOlderDone: false })}>Só as últimas 2 semanas</button>}
            </div>
            {ws.canWrite && col.id !== 'done' && (
              <div className="col-add">
                {adding === col.id
                  ? <CaptureField variant="add" autoFocus placeholder="Título, prazo, #projeto, @pessoa" label={`Nova tarefa em ${col.name}`}
                      onSubmit={async r => !!(await actions.createFromCapture(r, col.id))} onEscape={() => setAdding(null)} />
                  : <button className="add-btn" onClick={() => setAdding(col.id)}><Icon name="plus" />Nova tarefa</button>}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
